import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  HomeIcon,
  DocumentTextIcon,
  CreditCardIcon,
  UserGroupIcon,
  CogIcon,
  ChartBarIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../UI/Logo';

const MobileMenu = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const location = useLocation();

  const mutualiteNavigation = [
    { name: 'Tableau de bord', href: '/', icon: HomeIcon },
    { name: 'Produits', href: '/offers', icon: CreditCardIcon },
    { name: 'Mes bénéficiaires', href: '/beneficiaries', icon: UserGroupIcon },
    { name: 'Mes souscriptions', href: '/subscriptions', icon: DocumentTextIcon },
    { name: 'Mes polices', href: '/policies', icon: ShieldCheckIcon },
    { name: 'Mon profil', href: '/profile', icon: CogIcon },
  ];

  const adminNavigation = [
    { name: 'Tableau de bord', href: '/', icon: ChartBarIcon },
    { name: 'Mutualistes', href: '/admin/mutualistes', icon: UsersIcon },
    { name: 'Souscriptions', href: '/admin/subscriptions', icon: DocumentTextIcon },
    { name: 'Polices', href: '/admin/policies', icon: ShieldCheckIcon },
    { name: 'Demandes', href: '/admin/requests', icon: ClipboardDocumentListIcon },
    { name: 'Paiements', href: '/payments/unified', icon: BanknotesIcon },
    { name: 'Paramétrage', href: '/admin/parametrage', icon: WrenchScrewdriverIcon },
    { name: 'Mon profil', href: '/profile', icon: CogIcon },
  ];

  // Menu pour AGENT et SUPERVISEUR (comme admin mais sans Paramétrage + Produits)
  const agentSuperviseurNavigation = [
    { name: 'Tableau de bord', href: '/', icon: ChartBarIcon },
    { name: 'Produits', href: '/offers', icon: CreditCardIcon },
    { name: 'Mutualistes', href: '/admin/mutualistes', icon: UsersIcon },
    { name: 'Souscriptions', href: '/admin/subscriptions', icon: DocumentTextIcon },
    { name: 'Polices', href: '/admin/policies', icon: ShieldCheckIcon },
    { name: 'Demandes', href: '/admin/requests', icon: ClipboardDocumentListIcon },
    { name: 'Paiements', href: '/payments/unified', icon: BanknotesIcon },
    { name: 'Mon profil', href: '/profile', icon: CogIcon },
  ];

  const getNavigation = () => {
    if (user?.role === 'ADMIN') return adminNavigation;
    if (user?.role === 'AGENT' || user?.role === 'SUPERVISEUR') return agentSuperviseurNavigation;
    return mutualiteNavigation;
  };

  const navigation = getNavigation();

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
        onClick={onClose}
      />
      
      {/* Menu */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white lg:hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <Logo size="small" />
            <button
              type="button"
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              // Vérifier si c'est le menu Paramétrage et si on est sur une sous-page
              const isParametrageActive = item.name === 'Paramétrage' && 
                (location.pathname === item.href || 
                 location.pathname.startsWith('/admin/users') ||
                 location.pathname.startsWith('/admin/status') ||
                 location.pathname.startsWith('/admin/genres') ||
                 location.pathname.startsWith('/admin/fonctions') ||
                 location.pathname.startsWith('/admin/entreprises') ||
                 location.pathname.startsWith('/admin/lien-parente') ||
                 location.pathname.startsWith('/admin/situation-matrimoniale'));
              
              // Vérifier si c'est le menu Paiements et si on est sur une sous-page
              const isPaiementsActive = item.name === 'Paiements' && 
                (location.pathname.startsWith('/admin/payments') ||
                 location.pathname.startsWith('/payments/') ||
                 location.pathname === '/contribution-calls' ||
                 location.pathname === '/enterprise-payment-config');
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive || isParametrageActive || isPaiementsActive
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className={`mr-3 flex-shrink-0 h-6 w-6 ${
                          isActive || isParametrageActive || isPaiementsActive 
                            ? 'text-primary-600' 
                            : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                      />
                      {item.name}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* User info */}
          <div className="flex-shrink-0 px-4 py-4 border-t border-gray-200">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.mutualiste 
                    ? `${user.mutualiste.nom?.[0] || ''}${user.mutualiste.prenoms?.[0] || ''}`
                    : user?.role === 'ADMIN' 
                      ? 'AD'
                      : user?.role === 'AGENT'
                        ? 'AG'
                      : user?.role === 'SUPERVISEUR'
                        ? 'SU'
                        : user?.email?.[0]?.toUpperCase() || 'U'
                  }
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.mutualiste 
                    ? `${user.mutualiste.nom} ${user.mutualiste.prenoms}`
                    : user?.role === 'ADMIN' 
                      ? 'Administrateur'
                      : user?.role === 'AGENT'
                        ? 'Agent'
                      : user?.role === 'SUPERVISEUR'
                        ? 'Superviseur'
                        : user?.email
                  }
                </p>
                <p className="text-xs text-gray-500">
                  {user?.mutualiste 
                    ? `${user.mutualiste.matricule}` 
                    : user?.role === 'ADMIN' 
                      ? 'Administrateur' 
                      : user?.role === 'AGENT'
                        ? 'Agent'
                      : user?.role === 'SUPERVISEUR'
                        ? 'Superviseur'
                        : 'Utilisateur'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
